import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getAccountCloudType } from "../../../../CloudProviderContext";
import { fetchIamStack } from "../../../../store/remediation/remediationSlice";
import { friendlyErrorMessage, pickCustomerMessage } from "../common/ErrorCodeMap";
import { useRemediationSnackbar } from "../common/SnackbarHost";
import { useCloudAccounts } from "../common/useCloudAccounts";

/**
 * Customer-facing message for GET /tenant/iam-stack failures.
 * Prefers API customer_message; then 404/409 status copy for account not found,
 * inactive account, or incomplete onboarding; then detail / shared mapper.
 */
const iamStackErrorMessage = (err) => {
  if (!err) return "Something went wrong.";
  const customer = pickCustomerMessage(err);
  if (customer) return customer;

  if (err.status === 404) {
    return "We couldn't find that AWS account. Confirm the 12-digit account number is registered in CloudCatcher.";
  }
  if (err.status === 409) {
    return "This account isn't ready for the IAM stack — it may be inactive or onboarding is incomplete. Finish account setup, then try again.";
  }

  if (typeof err.detail === "string" && err.detail.trim()) return err.detail.trim();

  return friendlyErrorMessage(err);
};

const TOOLBAR_CONTROL_HEIGHT = 40;
const CC_BLUE = "#1976d2";
const CC_BLUE_HOVER = "#1565c0";

/**
 * Tenant IAM Stack tab.
 *
 * Account list comes from the shared org/accounts store (same `getOrgsAndAccts`
 * response as Configuration → Cloud Accounts). No duplicate accounts API here.
 * Template is fetched with account_number; the API fills RemediationRoleName + ExternalId.
 */
const IamStackPanel = () => {
  const dispatch = useDispatch();
  const { showSnackbar } = useRemediationSnackbar();
  const iamStack = useSelector((s) => s.remediation.iamStack);
  const { accounts, loading: accountsLoading } = useCloudAccounts();

  // Same AWS-only filter as CSPM / Compliance account dropdowns.
  const awsAccounts = useMemo(
    () => accounts.filter((acc) => getAccountCloudType(acc) === "AWS"),
    [accounts]
  );

  const [accountNumber, setAccountNumber] = useState("");

  useEffect(() => {
    if (awsAccounts.length === 0) {
      if (accountNumber) setAccountNumber("");
      return;
    }
    const stillValid = awsAccounts.some((a) => a.accountNumber === accountNumber);
    if (!stillValid) {
      setAccountNumber(awsAccounts[0].accountNumber);
    }
  }, [awsAccounts, accountNumber]);

  const handleAccountChange = (e) => {
    setAccountNumber(e.target.value);
  };

  useEffect(() => {
    if (!accountNumber) return;
    dispatch(fetchIamStack(String(accountNumber).trim()));
  }, [accountNumber, dispatch]);

  const handleRegenerate = () => {
    if (!accountNumber) {
      showSnackbar({ message: "Select an AWS account.", severity: "error" });
      return;
    }
    dispatch(fetchIamStack(String(accountNumber).trim()));
  };

  const handleCopy = async () => {
    if (!iamStack.template) return;
    try {
      await navigator.clipboard.writeText(iamStack.template);
      showSnackbar({ message: "Template copied to clipboard.", severity: "success" });
    } catch (_e) {
      showSnackbar({ message: "Couldn't copy — please select and copy manually.", severity: "error" });
    }
  };

  const handleDownload = () => {
    if (!iamStack.template) return;
    const blob = new Blob([iamStack.template], { type: "text/yaml;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download =
      iamStack.filename ||
      `cc-remediation-${iamStack.account_number || accountNumber || "stack"}.yaml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  return (
    <Box sx={{ pb: 4, mb: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle sx={{ mb: 0.5 }}>How to use this</AlertTitle>
        <Box component="ol" sx={{ pl: 2, m: 0, fontSize: 13 }}>
          <li>Select the AWS account you are deploying into.</li>
          <li>Copy the template or download the YAML below.</li>
          <li>
            In the AWS console, open <strong>CloudFormation → Create stack → With new resources</strong>{" "}
            and paste the template into <em>Template body</em>.
          </li>
        </Box>
      </Alert>

      {accountsLoading && awsAccounts.length === 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading accounts…
          </Typography>
        </Box>
      )}

      {!accountsLoading && awsAccounts.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {accounts.length === 0 ? (
            <>
              Account list is not loaded yet. Open{" "}
              <strong>Configuration → Cloud Accounts</strong> first (or refresh this page after
              accounts load elsewhere), then return here.
            </>
          ) : (
            <>No AWS accounts found for this organization.</>
          )}
        </Alert>
      )}

      <Stack spacing={0.75} sx={{ mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          flexWrap="wrap"
        >
          <FormControl size="small" sx={{ minWidth: 280 }} disabled={awsAccounts.length === 0}>
            <InputLabel id="iam-stack-account-label">AWS account</InputLabel>
            <Select
              labelId="iam-stack-account-label"
              label="AWS account"
              value={accountNumber}
              onChange={handleAccountChange}
              sx={{ height: TOOLBAR_CONTROL_HEIGHT }}
            >
              {awsAccounts.map((a) => (
                <MenuItem key={a.accountNumber} value={a.accountNumber}>
                  {a.accountName
                    ? `${a.accountName} (${a.accountNumber})`
                    : a.accountNumber}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            onClick={handleRegenerate}
            startIcon={<RefreshIcon />}
            variant="outlined"
            size="small"
            disabled={!accountNumber || awsAccounts.length === 0}
            sx={{ flexShrink: 0, height: TOOLBAR_CONTROL_HEIGHT }}
          >
            Generate template
          </Button>

          <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />

          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              onClick={handleCopy}
              startIcon={<ContentCopyIcon />}
              variant="outlined"
              size="small"
              disabled={iamStack.status !== "succeeded"}
              sx={{
                height: TOOLBAR_CONTROL_HEIGHT,
                color: CC_BLUE,
                borderColor: CC_BLUE,
                "&:hover": {
                  color: CC_BLUE_HOVER,
                  borderColor: CC_BLUE_HOVER,
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                },
              }}
            >
              Copy
            </Button>
            <Button
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
              variant="contained"
              size="small"
              disabled={iamStack.status !== "succeeded"}
              sx={{
                height: TOOLBAR_CONTROL_HEIGHT,
                backgroundColor: CC_BLUE,
                "&:hover": { backgroundColor: CC_BLUE_HOVER },
              }}
            >
              Download YAML
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {iamStack.status === "failed" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {iamStackErrorMessage(iamStack.error)}
        </Alert>
      )}

      {iamStack.status === "loading" ? (
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1, mb: 2 }} />
      ) : (
        <Box sx={{ position: "relative", mb: 2 }}>
          <TextField
            multiline
            fullWidth
            minRows={18}
            maxRows={28}
            value={iamStack.template || ""}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: "monospace", fontSize: 12.5, backgroundColor: "grey.50" },
            }}
          />
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, mb: 1 }}>
        The template is generated for the selected account. Remediation role name and External ID
        are filled in from your CloudCatcher account record.
      </Typography>
    </Box>
  );
};

export default IamStackPanel;
